import { ReactNode } from "react";

// Line icons for the capture card and the annotation editor. Inline (not
// asset files) so they inherit `currentColor` and render identically in the
// app build and in Storybook.
interface CaptureIconProps {
  className?: string;
}

const LineIcon = ({
  className,
  children,
}: CaptureIconProps & { children: ReactNode }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export const CameraIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z" />
    <circle cx="12" cy="13" r="3.2" />
  </LineIcon>
);

export const ScreenRecordIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <rect x="2" y="4" width="20" height="13" rx="2" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
    <circle cx="12" cy="10.5" r="2.4" fill="currentColor" stroke="none" />
  </LineIcon>
);

export const PaperclipIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </LineIcon>
);

export const CheckIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M20 6 9 17l-5-5" />
  </LineIcon>
);

export const AlertIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </LineIcon>
);

export const InfoIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <circle cx="12" cy="12" r="9.5" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </LineIcon>
);

// Diagnostics (the "logs were shared" system line).
export const ActivityIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M22 12h-4l-3 8L9 4l-3 8H2" />
  </LineIcon>
);

export const PlayCircleIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <circle cx="12" cy="12" r="9.5" />
    <path d="m10 8.5 5.5 3.5-5.5 3.5z" fill="currentColor" />
  </LineIcon>
);

export const PenIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M21.17 6.81a1 1 0 0 0-3.99-3.99L3.84 16.17a2 2 0 0 0-.5.83l-1.32 4.35a.5.5 0 0 0 .62.62l4.35-1.32a2 2 0 0 0 .83-.5z" />
    <path d="m15 5 4 4" />
  </LineIcon>
);

export const ArrowIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M6 18 18 6" />
    <path d="M9 6h9v9" />
  </LineIcon>
);

export const BoxIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <rect x="3.5" y="5" width="17" height="14" rx="2" />
  </LineIcon>
);

export const TextIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M5 7V5h14v2" />
    <path d="M12 5v14" />
    <path d="M9 19h6" />
  </LineIcon>
);

export const BlurIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="2" />
    <path d="M3.5 9.2h17M3.5 14.8h17M9.2 3.5v17M14.8 3.5v17" />
  </LineIcon>
);

export const CropIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M6 2v14a2 2 0 0 0 2 2h14" />
    <path d="M18 22V8a2 2 0 0 0-2-2H2" />
  </LineIcon>
);

export const UndoIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
  </LineIcon>
);

export const RedoIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="m15 14 5-5-5-5" />
    <path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
  </LineIcon>
);

export const CloseIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </LineIcon>
);

export const RetakeIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </LineIcon>
);

export const SendIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M14.54 21.69a.5.5 0 0 0 .94-.03l6.5-19a.5.5 0 0 0-.64-.63l-19 6.5a.5.5 0 0 0-.02.93l7.93 3.18a2 2 0 0 1 1.11 1.11z" />
    <path d="m21.85 2.15-10.94 10.94" />
  </LineIcon>
);

// Capture UI v2 (design-handoffs/capture-ui).
export const PlayIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path
      d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z"
      fill="currentColor"
      stroke="none"
    />
  </LineIcon>
);

export const ImageFileIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <circle cx="10" cy="13" r="1.6" />
    <path d="m19 17-3.5-3.5L9 20" />
  </LineIcon>
);

export const ArrowUpIcon = ({ className }: CaptureIconProps) => (
  <LineIcon className={className}>
    <path d="M12 19V5" />
    <path d="m5 12 7-7 7 7" />
  </LineIcon>
);
